import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type { DropdownItem } from "@/modules/dropdowns/types";
import type {
  AddMaintenancePayload,
  AssetComment,
  AssetDetail,
  AssetListParams,
  AssetListResponse,
  AssetAssignment,
  AssetMaintenance,
  AssetStats,
  AssignAssetPayload,
  CreateAssetPayload,
  UpdateAssetPayload,
} from "../types";

const ASSETS_KEY = ["assets"] as const;
const STATS_KEY = ["assets", "stats"] as const;

export function useAssetList(
  params: AssetListParams = {},
): UseQueryResult<AssetListResponse> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ASSETS_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<AssetListResponse>("/assets", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          category: params.category,
          status: params.status,
          condition: params.condition,
          location: params.location,
          assignedToId: params.assignedToId,
          projectId: params.projectId,
          companyId: params.companyId,
          warrantyExpiringSoon: params.warrantyExpiringSoon
            ? "true"
            : undefined,
        },
      }),
  });
}

export function useAsset(id: string): UseQueryResult<AssetDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ASSETS_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ asset: AssetDetail }>(`/assets/${id}`, {
        token: accessToken,
      });
      return res.asset;
    },
  });
}

export function useAssetStats(): UseQueryResult<AssetStats> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...STATS_KEY, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<AssetStats>("/assets/stats", { token: accessToken }),
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ASSETS_KEY });
  qc.invalidateQueries({ queryKey: STATS_KEY });
}

export function useCreateAsset() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAssetPayload) =>
      apiFetch<{ asset: AssetDetail }>("/assets", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateAsset(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateAssetPayload) =>
      apiFetch<{ asset: AssetDetail }>(`/assets/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateAssetStatus(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: string) =>
      apiFetch<{ asset: AssetDetail }>(`/assets/${id}/status`, {
        method: "PUT",
        body: { status },
        token: accessToken,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAssignAsset(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AssignAssetPayload) =>
      apiFetch<{ asset: AssetDetail }>(`/assets/${id}/assign`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useReturnAsset(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notes: string | undefined) =>
      apiFetch<{ asset: AssetDetail }>(`/assets/${id}/return`, {
        method: "PUT",
        body: { notes: notes ?? null },
        token: accessToken,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAddMaintenance(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddMaintenancePayload) =>
      apiFetch<{ record: AssetMaintenance }>(`/assets/${id}/maintenance`, {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteAsset() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/assets/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAssetHistory(id: string): UseQueryResult<AssetAssignment[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ASSETS_KEY, id, "history", accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ data: AssetAssignment[] }>(
        `/assets/${id}/history`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function useAssetMaintenance(
  id: string,
): UseQueryResult<AssetMaintenance[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ASSETS_KEY, id, "maintenance", accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ data: AssetMaintenance[] }>(
        `/assets/${id}/maintenance`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function useAssetComments(id: string): UseQueryResult<AssetComment[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ASSETS_KEY, id, "comments", accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ data: AssetComment[] }>(
        `/assets/${id}/comments`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function useAddAssetComment(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiFetch<{ comment: AssetComment }>(`/assets/${id}/comments`, {
        method: "POST",
        body: { content },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ASSETS_KEY, id, "comments"] });
    },
  });
}

export function useDeleteAssetComment(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiFetch(`/assets/${id}/comments/${commentId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ASSETS_KEY, id, "comments"] });
    },
  });
}

export function useAssetCategories(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("asset_category");
}

export function useAssetConditions(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("asset_condition");
}

export function useAssetLocations(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("asset_location");
}
